// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import {
  CommercialAgreement,
  CommercialAgreement_Revoke_Result,
} from '@daml.js/utility-commercials-v0/lib/Utility/Commercials/V0/Model/CommercialAgreement';
import { ContractId, DisclosedContract } from '@daml/types';
import { useLedgerApiClient } from '../../../contexts/LedgerApiContext';
import { useParties } from '../../../contexts/PartiesContext';
import { useScanProxyApiClient } from '../../../contexts/ScanProxyApiContext';
import { appTransferContextWithDisclosures } from '../../../utils/appTransferContextWithDisclosures';
import { getTransferPreapproval } from '../../../utils/getTransferPreapproval';

export const useRevokeCommercialAgreement: () => UseMutationResult<
  CommercialAgreement_Revoke_Result,
  string,
  {
    commercialAgreementCid: ContractId<CommercialAgreement>;
    feeReceiver: string;
    party: string; // could be user or operator
  }
> = () => {
  const ledgerApi = useLedgerApiClient();
  const scanProxyApi = useScanProxyApiClient();
  const { party, operator } = useParties();
  const enableAccruedFees = useIsPackageAvailableFrom(
    'utility-commercials-v0',
    [party, operator],
    '0.3.0',
  );

  return useMutation({
    mutationKey: ['revokeCommercialAgreement', ledgerApi],
    mutationFn: async ({ commercialAgreementCid, party: callingParty, feeReceiver }) => {
      const { appTransferContext, disclosedContracts } = await appTransferContextWithDisclosures(
        scanProxyApi,
        30,
      );
      let transferPreapprovalContractId = null;
      let disclosedPreapproval: DisclosedContract[] = [];
      if (enableAccruedFees.data) {
        const result = await getTransferPreapproval(scanProxyApi, feeReceiver);
        transferPreapprovalContractId = result.transferPreapproval.contractId;
        disclosedPreapproval = result.disclosedPreapproval;
      }
      return ledgerApi.revokeCommercialAgreement(
        commercialAgreementCid,
        appTransferContext,
        transferPreapprovalContractId,
        callingParty,
        [...disclosedContracts, ...disclosedPreapproval],
      );
    },
  });
};
